/**
 * Detalle platform-admin de un Estudio Contable: cartera de empresas, equipo
 * (staff) y solicitudes de alta, con todas las acciones de gestión que Zenlatam
 * necesita para operar un estudio sin shell (cierre del #105).
 *
 * ⚠️ EXENTO del barrido al `DataTable`, a proposito. Sus tres `<table>` NO
 * tienen `<thead>`: son listas de dos columnas —contenido a la izquierda,
 * accion a la derecha— y no tablas de datos. Migrarlas les agregaria
 * encabezados de columna que hoy no tienen, o sea cambiaria la pantalla para
 * peor. Medido sobre el producto: es el unico archivo asi de los 103 pendientes.
 */
import { useState } from 'react'
import {
  Building2, Users, Inbox, Plus, Power, Unlink, Pencil, ShieldCheck, UserCog,
  Loader2, Link2,
  type LucideIcon,
} from 'lucide-react'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { Button } from '@/components/Button'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { getApiErrorMessage } from '@/lib/apiClient'
import type { FirmRole, FirmClientRequest } from '@/modules/firm/hooks/useFirm'
import {
  useFirmAdminDetail, useUpdateFirm, useLinkFirmClient, useUpdateFirmClientAdmin,
  useUnlinkFirmClient, useAddFirmMember, useUpdateFirmMember, useResolveRequest,
} from '@/modules/firm/hooks/useFirmAdmin'
import { UserEmailPicker, FirmStatusBadge, TenantPicker, adminInputCls } from './firmAdminShared'

type Toast = { success: (m: string) => void; error: (m: string) => void }

const FIRM_ROLE_LABEL: Record<FirmRole, string> = {
  firm_admin: 'Administrador',
  firm_staff: 'Staff',
}
const REQ_STATUS: Record<string, { cls: string; label: string }> = {
  pending: { cls: 'bg-warning-surface text-warning-content', label: 'Pendiente' },
  approved: { cls: 'bg-success-surface text-success-content', label: 'Aprobada' },
  rejected: { cls: 'bg-danger-surface text-danger-content', label: 'Rechazada' },
}

// ─── Sub-modal: vincular empresa a la cartera ──────────────────────────────────

function LinkClientModal({ firmId, onClose, toast }: { firmId: string; onClose: () => void; toast: Toast }) {
  const link = useLinkFirmClient()
  const [schema, setSchema] = useState('')
  const [name, setName] = useState('')
  const [isOwn, setIsOwn] = useState(false)

  const submit = async () => {
    if (!schema) return
    try {
      await link.mutateAsync({ firm_id: firmId, tenant_schema: schema, tenant_name: name, is_own_company: isOwn })
      toast.success('Empresa vinculada a la cartera.')
      onClose()
    } catch (e) { toast.error(getApiErrorMessage(e)) }
  }

  return (
    <Modal open onClose={onClose} title="Vincular empresa" size="sm" actions={
      <>
        <ModalCancelButton onClick={onClose} />
        <Button onClick={submit} disabled={!schema} isPending={link.isPending} variant="primary" size="lg">Vincular</Button>
      </>
    }>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-content mb-1">Empresa (tenant)</label>
          <TenantPicker value={schema} onChange={(s, n) => { setSchema(s); setName(n) }} />
        </div>
        <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
          <input type="checkbox" checked={isOwn} onChange={(e) => setIsOwn(e.target.checked)}
            className="rounded border-divider-strong text-primary focus:ring-primary" />
          Es la empresa propia del estudio (se gestiona a sí mismo)
        </label>
      </div>
    </Modal>
  )
}

// ─── Sub-modal: agregar miembro al equipo ──────────────────────────────────────

function AddMemberModal({ firmId, onClose, toast }: { firmId: string; onClose: () => void; toast: Toast }) {
  const addMember = useAddFirmMember()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<FirmRole>('firm_staff')

  const submit = async () => {
    if (!email.trim()) return
    try {
      await addMember.mutateAsync({ firmId, payload: { email: email.trim(), firm_role: role } })
      toast.success('Miembro agregado al estudio.')
      onClose()
    } catch (e) { toast.error(getApiErrorMessage(e)) }
  }

  return (
    <Modal open onClose={onClose} title="Agregar miembro" size="sm" actions={
      <>
        <ModalCancelButton onClick={onClose} />
        <Button onClick={submit} disabled={!email.trim()} isPending={addMember.isPending} variant="primary" size="lg">Agregar</Button>
      </>
    }>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-content mb-1">Usuario</label>
          <UserEmailPicker value={email} onChange={setEmail} autoFocus />
        </div>
        <div>
          <label htmlFor="firmadmindetailmodal-rol-en-el-estudio" className="block text-sm font-medium text-content mb-1">Rol en el estudio</label>
          <select id="firmadmindetailmodal-rol-en-el-estudio" value={role} onChange={(e) => setRole(e.target.value as FirmRole)} className={adminInputCls}>
            <option value="firm_staff">Staff del estudio</option>
            <option value="firm_admin">Administrador del estudio</option>
          </select>
        </div>
      </div>
    </Modal>
  )
}

// ─── Sub-modal: resolver una solicitud de alta ─────────────────────────────────

function ResolveRequestModal({ request, onClose, toast }: { request: FirmClientRequest; onClose: () => void; toast: Toast }) {
  const resolve = useResolveRequest()
  const [schema, setSchema] = useState(request.existing_tenant_schema || '')
  const [name, setName] = useState(request.company_name || '')

  const approve = async () => {
    if (!schema) return
    try {
      await resolve.mutateAsync({ id: request.id, payload: { action: 'approve', tenant_schema: schema, tenant_name: name } })
      toast.success('Solicitud aprobada y empresa vinculada.')
      onClose()
    } catch (e) { toast.error(getApiErrorMessage(e)) }
  }
  const reject = async () => {
    try {
      await resolve.mutateAsync({ id: request.id, payload: { action: 'reject' } })
      toast.success('Solicitud rechazada.')
      onClose()
    } catch (e) { toast.error(getApiErrorMessage(e)) }
  }

  return (
    <Modal open onClose={onClose} title={`Solicitud — ${request.company_name}`} size="sm" actions={
      <>
        <Button onClick={reject} isPending={resolve.isPending} variant="danger">Rechazar</Button>
        <Button onClick={approve} disabled={!schema} isPending={resolve.isPending} variant="primary" size="lg">
          Aprobar y vincular
        </Button>
      </>
    }>
      <div className="space-y-4">
        <div className="text-sm text-content-muted">
          <p><span className="text-content-subtle">Tipo:</span> {request.kind === 'new' ? 'Empresa nueva' : 'Vincular existente'}</p>
          {request.tax_id && <p><span className="text-content-subtle">RUT:</span> {request.tax_id}</p>}
          {request.notes && <p className="mt-1">{request.notes}</p>}
        </div>
        <div className="rounded-lg bg-soft/60 border border-divider p-3 text-xs text-content-muted">
          Para aprobar, primero aprovisiona el tenant en la pestaña <strong>Tenants</strong> (si es nuevo) y aquí
          selecciona el schema que quedará en la cartera del estudio.
        </div>
        <div>
          <label className="block text-sm font-medium text-content mb-1">Empresa a vincular</label>
          <TenantPicker value={schema} onChange={(s, n) => { setSchema(s); setName(n) }} />
        </div>
      </div>
    </Modal>
  )
}

// ─── Sub-modal: editar el estudio ──────────────────────────────────────────────

function EditFirmModal({ firm, onClose, toast }: { firm: { id: string; name: string; tax_id: string; billing_email: string }; onClose: () => void; toast: Toast }) {
  const update = useUpdateFirm()
  const [name, setName] = useState(firm.name)
  const [taxId, setTaxId] = useState(firm.tax_id)
  const [billingEmail, setBillingEmail] = useState(firm.billing_email)

  const submit = async () => {
    if (!name.trim()) return
    try {
      await update.mutateAsync({ id: firm.id, payload: { name: name.trim(), tax_id: taxId.trim(), billing_email: billingEmail.trim() } })
      toast.success('Estudio actualizado.')
      onClose()
    } catch (e) { toast.error(getApiErrorMessage(e)) }
  }

  return (
    <Modal open onClose={onClose} title="Editar estudio" size="sm" actions={
      <>
        <ModalCancelButton onClick={onClose} />
        <Button onClick={submit} disabled={!name.trim()} isPending={update.isPending} variant="primary" size="lg">Guardar</Button>
      </>
    }>
      <div className="space-y-4">
        <div>
          <label htmlFor="firmadmindetailmodal-razon-social" className="block text-sm font-medium text-content mb-1">Razón social *</label>
          <input id="firmadmindetailmodal-razon-social" value={name} onChange={(e) => setName(e.target.value)} className={adminInputCls} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firmadmindetailmodal-rut" className="block text-sm font-medium text-content mb-1">RUT</label>
            <input id="firmadmindetailmodal-rut" value={taxId} onChange={(e) => setTaxId(e.target.value)} className={adminInputCls} />
          </div>
          <div>
            <label htmlFor="firmadmindetailmodal-correo-de-facturacion" className="block text-sm font-medium text-content mb-1">Correo de facturación</label>
            <input id="firmadmindetailmodal-correo-de-facturacion" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} className={adminInputCls} />
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ─── Sección reutilizable ───────────────────────────────────────────────────────

function Section({ icon: Icon, title, action, children }: {
  icon: LucideIcon
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="border border-divider rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-divider bg-soft/40">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-content">
          <Icon size={15} className="text-content-muted" /> {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  )
}

// ─── Componente principal ────────────────────────────────────────────────────────

export function FirmAdminDetailModal({ firmId, onClose, toast }: { firmId: string; onClose: () => void; toast: Toast }) {
  const { data, isLoading } = useFirmAdminDetail(firmId)
  const updateFirm = useUpdateFirm()
  const toggleClient = useUpdateFirmClientAdmin()
  const unlink = useUnlinkFirmClient()
  const updateMember = useUpdateFirmMember()
  const { confirm, modalProps } = useConfirmModal()

  const [linkOpen, setLinkOpen] = useState(false)
  const [memberOpen, setMemberOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [resolveReq, setResolveReq] = useState<FirmClientRequest | null>(null)

  const firm = data?.firm
  const pendingReqs = (data?.requests ?? []).filter((r) => r.status === 'pending')
  const resolvedReqs = (data?.requests ?? []).filter((r) => r.status !== 'pending')

  const toggleFirmStatus = () => {
    if (!firm) return
    const next = firm.status === 'active' ? 'suspended' : 'active'
    confirm({
      message: next === 'suspended'
        ? `¿Suspender el estudio "${firm.name}"? Sus miembros dejarán de ver el portal del estudio.`
        : `¿Reactivar el estudio "${firm.name}"?`,
      variant: next === 'suspended' ? 'danger' : 'success',
      confirmLabel: next === 'suspended' ? 'Suspender' : 'Reactivar',
      onConfirm: async () => {
        try {
          await updateFirm.mutateAsync({ id: firm.id, payload: { status: next } })
          toast.success(next === 'suspended' ? 'Estudio suspendido.' : 'Estudio reactivado.')
        } catch (e) { toast.error(getApiErrorMessage(e)) }
      },
    })
  }

  return (
    <Modal open onClose={onClose} title={firm ? firm.name : 'Estudio Contable'} size="xl" actions={
      <Button variant="secondary" onClick={onClose}>Cerrar</Button>
    }>
      {isLoading || !firm ? (
        <div className="py-6 text-center text-content-subtle">
          <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Cargando estudio…
        </div>
      ) : (
        <div className="space-y-5">
          {/* Cabecera del estudio */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <FirmStatusBadge status={firm.status} />
                {firm.tax_id && <span className="text-sm text-content-muted">{firm.tax_id}</span>}
              </div>
              {firm.billing_email && <p className="text-xs text-content-subtle mt-1">{firm.billing_email}</p>}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)} leftIcon={<Pencil size={13} />}>Editar</Button>
              <Button variant={firm.status === 'active' ? 'ghost-warning' : 'success'} size="sm"
                onClick={toggleFirmStatus} leftIcon={<Power size={13} />}>
                {firm.status === 'active' ? 'Suspender' : 'Reactivar'}
              </Button>
            </div>
          </div>

          {/* Empresas (cartera) */}
          <Section icon={Building2} title={`Empresas (${data!.clients.length})`}
            action={<Button size="sm" variant="primary" onClick={() => setLinkOpen(true)} leftIcon={<Plus size={13} />}>Vincular empresa</Button>}>
            {data!.clients.length === 0 ? (
              <p className="px-4 py-6 text-sm text-content-subtle text-center">Sin empresas en la cartera.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody className="divide-y divide-divider">
                  {data!.clients.map((c) => (
                    <tr key={c.id} className="hover:bg-soft/40">
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-content">{c.tenant_name}</span>
                        {c.is_own_company && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-primary-50 text-primary-hover">Propia</span>}
                        <span className="block text-xs text-content-subtle">{c.tenant_schema}</span>
                      </td>
                      <td className="px-4 py-2.5 text-content-muted text-xs">
                        {c.assigned_to_detail?.full_name || <span className="text-content-subtle">Sin contador</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.is_active
                          ? 'bg-success-surface text-success-content'
                          : 'bg-surface-muted text-content-muted'}`}>
                          {c.is_active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button onClick={() => toggleClient.mutate({ id: c.id, payload: { is_active: !c.is_active } })}
                          className="px-2 py-1 text-xs text-content-muted hover:bg-soft rounded-lg inline-flex items-center gap-1">
                          <Power size={12} /> {c.is_active ? 'Desactivar' : 'Activar'}
                        </button>
                        <button onClick={() => confirm({
                            message: `¿Desvincular "${c.tenant_name}" de la cartera de ${firm.name}? El tenant queda intacto; solo se quita de este estudio.`,
                            variant: 'danger', confirmLabel: 'Desvincular',
                            onConfirm: async () => {
                              try { await unlink.mutateAsync(c.id); toast.success('Empresa desvinculada.') }
                              catch (e) { toast.error(getApiErrorMessage(e)) }
                            },
                          })}
                          className="px-2 py-1 text-xs text-danger-content-soft hover:bg-danger-surface rounded-lg inline-flex items-center gap-1">
                          <Unlink size={12} /> Desvincular
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Equipo (staff) */}
          <Section icon={Users} title={`Equipo (${data!.staff.length})`}
            action={<Button size="sm" variant="primary" onClick={() => setMemberOpen(true)} leftIcon={<Plus size={13} />}>Agregar miembro</Button>}>
            {data!.staff.length === 0 ? (
              <p className="px-4 py-6 text-sm text-content-subtle text-center">Sin miembros. Agrega al menos un administrador.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody className="divide-y divide-divider">
                  {data!.staff.map((m) => (
                    <tr key={m.id} className="hover:bg-soft/40">
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-content">{m.user_detail.full_name || m.user_detail.email}</span>
                        <span className="block text-xs text-content-subtle">{m.user_detail.email}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 text-xs text-content-muted">
                          {m.firm_role === 'firm_admin' ? <ShieldCheck size={13} className="text-warning-solid" /> : <UserCog size={13} />}
                          {FIRM_ROLE_LABEL[m.firm_role]}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        {m.status === 'revoked'
                          ? <span className="text-xs text-danger-content-soft">Revocado</span>
                          : <span className="text-xs text-success-content-soft">Activo</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button onClick={() => updateMember.mutate({ id: m.id, payload: {
                            firm_role: m.firm_role === 'firm_admin' ? 'firm_staff' : 'firm_admin',
                          } })}
                          className="px-2 py-1 text-xs text-content-muted hover:bg-soft rounded-lg">
                          {m.firm_role === 'firm_admin' ? '→ Staff' : '→ Admin'}
                        </button>
                        {m.status !== 'revoked' ? (
                          <button onClick={() => confirm({
                              message: `¿Revocar a ${m.user_detail.email} del estudio?`, variant: 'danger', confirmLabel: 'Revocar',
                              onConfirm: async () => {
                                try { await updateMember.mutateAsync({ id: m.id, payload: { status: 'revoked' } }); toast.success('Miembro revocado.') }
                                catch (e) { toast.error(getApiErrorMessage(e)) }
                              },
                            })}
                            className="px-2 py-1 text-xs text-danger-content-soft hover:bg-danger-surface rounded-lg">Revocar</button>
                        ) : (
                          <button onClick={() => updateMember.mutate({ id: m.id, payload: { status: 'accepted' } })}
                            className="px-2 py-1 text-xs text-success-content-soft hover:bg-success-surface rounded-lg">Reactivar</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* Solicitudes */}
          <Section icon={Inbox} title={`Solicitudes pendientes (${pendingReqs.length})`}>
            {pendingReqs.length === 0 ? (
              <p className="px-4 py-6 text-sm text-content-subtle text-center">Sin solicitudes pendientes.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody className="divide-y divide-divider">
                  {pendingReqs.map((r) => (
                    <tr key={r.id} className="hover:bg-soft/40">
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-content">{r.company_name}</span>
                        <span className="block text-xs text-content-subtle">
                          {r.kind === 'new' ? 'Empresa nueva' : 'Vincular existente'}{r.tax_id ? ` · ${r.tax_id}` : ''}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button size="sm" variant="primary" onClick={() => setResolveReq(r)} leftIcon={<Link2 size={13} />}>Resolver</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {resolvedReqs.length > 0 && (
              <div className="px-4 py-2 border-t border-divider">
                {resolvedReqs.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-1 text-xs">
                    <span className="text-content-muted">{r.company_name}</span>
                    <span className={`px-2 py-0.5 rounded-full font-medium ${(REQ_STATUS[r.status] ?? REQ_STATUS.pending).cls}`}>
                      {(REQ_STATUS[r.status] ?? REQ_STATUS.pending).label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Sub-modales */}
      {linkOpen && <LinkClientModal firmId={firmId} onClose={() => setLinkOpen(false)} toast={toast} />}
      {memberOpen && <AddMemberModal firmId={firmId} onClose={() => setMemberOpen(false)} toast={toast} />}
      {editOpen && firm && <EditFirmModal firm={firm} onClose={() => setEditOpen(false)} toast={toast} />}
      {resolveReq && <ResolveRequestModal request={resolveReq} onClose={() => setResolveReq(null)} toast={toast} />}
      <ConfirmModal {...modalProps} />
    </Modal>
  )
}
